import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { GlobalAdminComponents, GlobalConfigComponents, GlobalVariable } from '../helpers/settings';
import { Project } from '../models/project';
import { User } from '../models/user';
import { AuthService } from './auth.service';
import { RoleService } from './user-role.service';
import { Role } from '../models/user-role';
import { UserService } from './user.service';
import { Subscription } from '../models/subscription';
import { AppConfigService } from './app-config.service';

// Menu
export interface Menu {
	headTitle1?: string;
	headTitle2?: string;
	code?: string;
	path?: string;
	queryParams?: any;
	title?: string;
	icon?: string;
	imageIcon?: string;
	svgIcon?: string;
	logo?: string;
	type?: string;
	badgeType?: string;
	badgeValue?: string;
	active?: boolean;
	bookmark?: boolean;
	children?: Menu[];
	childApp?: Menu[];
	projectId?: string;
}

@Injectable({
	providedIn: 'root'
})
export class NavService implements OnDestroy, OnInit {
	private unsubscriber: Subject<any> = new Subject();
	public screenWidth: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);
	public dashboard: string;
	public projects$: Observable<Project[]>;
	public projects: Project[] = [];
	public currentUser: Partial<User> = {
		username: null,
		email: null,
		firstName: null,
		roles: [],
		userRole: { role: null },
		salary: null,
		lastName: null,
		phoneNumber: null,
		address: null,
		password: null,
		imageUrl: null,
		city: null,
		active: true,
		project: null
	};
	public ALL_ROUTES = [];
	private adminRoutes: any[] = [];
	public currentUserRole: Partial<Role> = {
		name: null,
		access: []
	};
	MENUITEMS: Menu[] = [];
	public menuSubject = new BehaviorSubject<Menu[]>(this.MENUITEMS);
	// Search Box
	public search: boolean = false;

	// Language
	public language: boolean = false;

	// Mega Menu
	public megaMenu: boolean = false;
	public levelMenu: boolean = false;
	public megaMenuColapse: boolean = window.innerWidth < 1199 ? true : false;

	// Collapse Sidebar
	public collapseSidebar: boolean = window.innerWidth < 991 ? true : false;

	// For Horizontal Layout Mobile
	public horizontal: boolean = window.innerWidth < 991 ? false : true;

	// Full screen
	public fullScreen: boolean = false;

	constructor(
		private router: Router,
		private authService: AuthService,
		private roleService: RoleService,
		private userService: UserService,
		private appConfig: AppConfigService
	) {
		this.setScreenWidth(window.innerWidth);
		fromEvent(window, 'resize')
			.pipe(debounceTime(1000), takeUntil(this.unsubscriber))
			.subscribe((evt: any) => {
				this.setScreenWidth(evt.target.innerWidth);
				if (evt.target.innerWidth < 991) {
					this.collapseSidebar = true;
					this.megaMenu = false;
					this.levelMenu = false;
				}
				if (evt.target.innerWidth < 1199) {
					this.megaMenuColapse = true;
				}
			});
		if (window.innerWidth < 991) {
			// Detect Route change sidebar close
			this.router.events.subscribe(event => {
				this.collapseSidebar = true;
				this.megaMenu = false;
				this.levelMenu = false;
			});
		}
		this.menuSubject.next(this.MENUITEMS);
	}

	ngOnInit() {
		this.initMenu();
	}

	ngOnDestroy() {
		// this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	private setScreenWidth(width: number): void {
		this.screenWidth.next(width);
	}

	getMenuItems(): Observable<Menu[]> {
		this.menuSubject.next([]);
		//this.initMenu();
		return this.menuSubject;
	}

	private initAllConfigRoutes() {
		this.adminRoutes = GlobalAdminComponents?.filter(configComponent => !configComponent.isChildComponent).map(
			app => {
				const route = { title: app.title, name: app.name, route: app.route, svgIcon: app?.svgIcon ?? null };
				return {
					...route,
					path: `/super-admin/${app.route}/`,
					type: 'link',
					roles: ['Admin', 'SuperAdmin']
				};
			}
		);
		this.ALL_ROUTES = GlobalConfigComponents?.filter(configComponent => !configComponent.isChildComponent).map(
			app => {
				const route = {
					title:
						app.isModel && !app.isChildModel && !app?.childComponents?.length
							? this.appConfig.getAppNickName(app?.appCode) + ' ' + 'Model'
							: app.title,
					name: app.name,
					route: app.route,
					isModel: app.isModel,
					appCode: app.appCode ?? null,
					svgIcon: app?.svgIcon ?? null
				};
				switch (app.route) {
					case 'applications':
						return {
							...route,
							path: `/admin/${app.route}/`,
							type: 'link',
							roles: ['Owner', 'Admin', 'SuperAdmin', 'User']
						};
						break;
					default:
						// return route result as format : { name, title, path, type }
						return {
							...route,
							path: `/admin/${app.route}/`,
							type: 'link',
							roles: ['Owner', 'Admin', 'SuperAdmin', 'User']
						};
				}
			}
		);
	}

	getChildrenByRole(isAdmin = false): any[] {
		if (isAdmin) {
			return this.adminRoutes
				.filter(route => {
					if (route.roles?.some(role => this.currentUser?.roles?.includes(role))) {
						if (
							this.currentUser?.roles?.includes('Admin') &&
							[`/super-admin/account-managers/`, `/super-admin/billings/`].includes(route.path)
						) {
							return false;
						}
						return true;
					}
					return false;
				})
				.map(route => {
					delete route.roles;
					return route;
				});
		}
		return this.ALL_ROUTES.filter(route => route.roles?.some(r => this.currentUser?.roles?.includes(r))).map(
			route => {
				delete route.roles;
				return route;
			}
		);
	}
	toUpperCaseWithUnderscores = input => {
		return input
			?.split('-')
			?.map(word => word.toUpperCase())
			.join('_');
	};

	initMenu() {
		this.currentUser = this.userService.getCurrentUserInfos();
		const currentSubscription = this.currentUser.subscription as Subscription;
		this.appConfig.load(this.currentUser);
		this.initAllConfigRoutes();
		if (this.ALL_ROUTES.length) {
			this.ALL_ROUTES = this.ALL_ROUTES.filter(route => {
				if (route?.isModel) {
					return currentSubscription?.applications.includes(route?.appCode);
				}
				return true;
			});
			if (this.currentUser.userRole?.role) {
				this.currentUserRole = this.currentUser.userRole?.role as Role;
				const hasAccessToComponents = this.currentUserRole.access.find(
					access =>
						access.config === 'Config' &&
						access.componentAccess.some(componentAccess => componentAccess?.isActive)
				);
				if (hasAccessToComponents && !this.roleService.hasOwnerAccess()) {
					this.ALL_ROUTES = this.ALL_ROUTES.filter(route => {
						const componentRoute = hasAccessToComponents.componentAccess.find(
							ComponentAccess => ComponentAccess.route === route.route
						);
						if (componentRoute?.isActive) {
							return true;
						}
						return false;
					});
					this.fillMenu();
				} else {
					this.ALL_ROUTES = [];
					this.fillMenu();
				}
			} else {
				this.fillMenu();
			}
		}
	}

	fillMenu() {
		this.MENUITEMS = [];
		if (this.ALL_ROUTES.length) {
			this.MENUITEMS = [
				{
					headTitle1: 'General'
				},
				{
					title: GlobalVariable['CONFIG'].title,
					icon: GlobalVariable['CONFIG'].icon,
					type: 'sub',
					badgeType: 'light-primary',
					badgeValue: '10',
					active: false,
					children: this.getChildrenByRole()
				}
			];
			if (this.currentUser.roles.some(role => ['Admin', 'SuperAdmin'].includes(role))) {
				if (!this.currentUser?.subscription?.toString()?.length) {
					this.MENUITEMS = [];
				}
				this.MENUITEMS.unshift(
					{
						headTitle1: 'Administration'
					},
					{
						title: GlobalVariable['ADMIN'].title,
						icon: GlobalVariable['ADMIN'].icon,
						type: 'sub',
						badgeType: 'light-primary',
						badgeValue: '10',
						active: false,
						children: this.getChildrenByRole(true)
					}
				);
			}
		}
		this.menuSubject.next(this.MENUITEMS);
	}
}
